import * as R from 'ramda'

/**
 * @typedef Filter
 * @property {string | number | boolean} value
 * @property {string=} label
 */

/**
 * @typedef {'is' | 'is_not' | 'contains' | 'does_not_contains' | 'is_unknown' | 'has_any_value'}  Comparison
 */

/**
 * @template T
 * @typedef InputProps
 * @property {(filters: Filter[]) => void} onChange
 * @property {Filter[]} defaultFilters
 * @property {() => void} [onClose]
 * @property {Rule<T>} [rule]
 * @property {boolean} isMultipleValue
 */

/**
 * @template T
 * @typedef Rule
 * @property {string} field
 * @property {string} label
 * @property {import('semantic-ui-react').SemanticICONS} icon
 * @property {string} comparison
 * @property {Filter[]} filters
 * @property {React.FunctionComponent<InputProps<T>> | React.ComponentClass<InputProps<T>>} [inputComponent]
 * @property {T} [extra]
 * @property {boolean} [openPopoverOnAdd]
 */

/**
 * @template T
 * @typedef Action
 * @property {'CLICK_ADD_RULE' | 'CLOSE_DROPDOWN' | 'ADD_RULE' | 'SET_FILTER' | 'CLOSE_POPOVER' | 'OPEN_POPOVER' | 'REMOVE_RULE' | 'SET_COMPARISON' | 'SET_EXTRA'} type
 * @property {Rule<T>=} rule
 * @property {Filter[]=} filters
 * @property {number=} index
 * @property {string=} comparison
 */

/**
 * @typedef {ReturnType<typeof filterReducer.createActions>} Actions
 */

/**
 * @template T
 * @callback Reducer
 * @param {State<T>} state
 * @param {Action<T>} action
 * @returns {State<T>}
 */

/**
 * @template T
 * @typedef State
 * @property {boolean} isDropdownOpen
 * @property {number} activePopoverIndex
 * @property {Rule<T>[]} rules
 */

const filterReducer = {
  initialState: {
    isDropdownOpen: false,
    activePopoverIndex: -1,
    rules: [],
  },
  /**
   * @template T
   * @type {Reducer<T>}
   */
  reducer(state, action) {
    if (action.type === 'CLICK_ADD_RULE') {
      return { ...state, isDropdownOpen: true, activePopoverIndex: -1 }
    }

    if (action.type === 'CLOSE_DROPDOWN') {
      return {
        ...state,
        isDropdownOpen: false,
      }
    }

    if (action.type === 'CLOSE_POPOVER') {
      // Not closing the popover open by other FilterItem
      if (action.index !== state.activePopoverIndex) {
        return state
      }

      return {
        ...state,
        activePopoverIndex: -1,
      }
    }

    if (action.type === 'OPEN_POPOVER') {
      return {
        ...state,
        activePopoverIndex: action.index,
      }
    }

    if (action.type === 'ADD_RULE') {
      const rules = state.rules.concat(action.rule)
      const isInstantOption = !R.isEmpty(action.rule.filters) && !action.rule.openPopoverOnAdd
      if (isInstantOption) {
        return { ...state, rules, isDropdownOpen: false, activePopoverIndex: -1 }
      }

      const latestRuleIndex = rules.length - 1
      return { ...state, isDropdownOpen: false, rules, activePopoverIndex: latestRuleIndex }
    }

    if (action.type === 'REMOVE_RULE') {
      return {
        ...state,
        rules: R.remove(action.index, 1, state.rules),
      }
    }

    if (action.type === 'SET_FILTER') {
      const selectedRule = state.rules[state.activePopoverIndex]
      const newRule = {
        ...selectedRule,
        filters: action.filters,
      }

      return {
        ...state,
        rules: R.update(state.activePopoverIndex, newRule, state.rules),
      }
    }

    if (action.type === 'SET_COMPARISON') {
      const selectedRule = state.rules[state.activePopoverIndex]
      const newRule = {
        ...selectedRule,
        comparison: action.comparison,
      }

      return {
        ...state,
        rules: R.update(state.activePopoverIndex, newRule, state.rules),
      }
    }

    if (action.type === 'SET_EXTRA') {
      const selectedRule = state.rules[state.activePopoverIndex]
      const newRule = {
        ...selectedRule,
        extra: action.extra,
      }

      return {
        ...state,
        rules: R.update(state.activePopoverIndex, newRule, state.rules),
      }
    }

    return state
  },
  /**
   * @template T
   * @param {(action: Action<T>) => void} dispatch
   */
  createActions(dispatch) {
    const actions = {
      clickAddRule() {
        dispatch({
          type: 'CLICK_ADD_RULE',
        })
      },
      closeDropdown() {
        dispatch({
          type: 'CLOSE_DROPDOWN',
        })
      },
      /**
       * @param {Rule<T>} rule
       */
      addRule(rule) {
        dispatch({
          type: 'ADD_RULE',
          rule,
        })
      },
      /** @param {number} index */
      removeRule(index) {
        dispatch({
          type: 'REMOVE_RULE',
          index,
        })
      },
      /**
       * @param {Filter[]} filters
       */
      setFilter(filters) {
        dispatch({
          type: 'SET_FILTER',
          filters,
        })
      },
      /**
       * @param {Comparison} comparison
       */
      setComparison(comparison) {
        dispatch({
          type: 'SET_COMPARISON',
          comparison,
        })
      },
      /** @param {number} index */
      closePopover(index) {
        dispatch({
          type: 'CLOSE_POPOVER',
          index,
        })
      },
      /** @param {number} index */
      openPopover(index) {
        dispatch({
          type: 'OPEN_POPOVER',
          index: index,
        })
      },
      setExtra(extra) {
        dispatch({
          type: 'SET_EXTRA',
          extra,
        })
      },
    }

    return actions
  },
}

export default filterReducer



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/shared/components/FilterGroup/reducer.js